import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  afterRenderEffect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ThemeService } from '@webapp/shared';

import { type Theme } from '../../services';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  energy: number;
}

interface Palette {
  dotBase: [number, number, number, number];
  dotHot: [number, number, number, number];
  line: [number, number, number];
  lineBaseA: number;
  ring: [number, number, number];
  coreA: number;
}

const PALETTES: Record<Theme, Palette> = {
  dark: {
    dotBase: [120, 210, 245, 0.85],
    dotHot: [220, 245, 255, 1],
    line: [42, 196, 245],
    lineBaseA: 0.22,
    ring: [42, 196, 245],
    coreA: 0.1,
  },
  light: {
    dotBase: [0, 137, 191, 0.65],
    dotHot: [0, 137, 191, 1],
    line: [0, 137, 191],
    lineBaseA: 0.18,
    ring: [0, 137, 191],
    coreA: 0.06,
  },
};

const PARTICLE_COUNT = 60;
const LINK_DIST = 130;
const CENTER_GUARD = 56;
const PULSE_PERIOD = 2200;
const PULSE_BAND = 48;

// Particles are kept in a cluster around the center (the X logo) rather than spread
// across the whole viewport. A baseline radius plus a gentle per-pixel growth keeps the
// cloud from getting too dense on small screens, while the cap keeps it tight and
// connected on large ones (where the growth term would otherwise overshoot).
const CLUSTER_RADIUS_BASE = 90;
const CLUSTER_RADIUS_RATIO = 0.3;
const MAX_CLUSTER_RADIUS = 340;
const MAX_PARTICLE_SPEED = 0.5;

const LOADING_MESSAGES = [
  'app.loader.messages.0',
  'app.loader.messages.1',
  'app.loader.messages.2',
  'app.loader.messages.3',
  'app.loader.messages.4',
  'app.loader.messages.5',
];

const MESSAGE_INTERVAL_MS = 2000;

function clusterRadiusFor(width: number, height: number): number {
  const radius = CLUSTER_RADIUS_BASE + Math.min(width, height) * CLUSTER_RADIUS_RATIO;

  return Math.max(CENTER_GUARD + 60, Math.min(radius, MAX_CLUSTER_RADIUS));
}

@Component({
  selector: 'knx-app-loader',
  imports: [TranslatePipe],
  templateUrl: './app-loader.html',
})
export class AppLoader {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly themeService = inject(ThemeService);

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly containerRef = viewChild.required<ElementRef<HTMLDivElement>>('container');

  protected readonly messageIndex = signal(0);
  protected readonly messages = LOADING_MESSAGES;

  private meshRafId = 0;
  private meshResizeObserver?: ResizeObserver;
  private messageIntervalId = 0;

  constructor() {
    // Rotate loading messages on a fixed interval.
    afterNextRender(() => {
      this.messageIntervalId = window.setInterval(() => {
        this.messageIndex.update((i) => (i + 1) % LOADING_MESSAGES.length);
      }, MESSAGE_INTERVAL_MS);
    });

    // (Re)build the canvas mesh whenever the theme changes.
    afterRenderEffect(() => {
      const theme = this.themeService.theme();
      this.stopMesh();
      this.startMesh(theme);
    });

    this.destroyRef.onDestroy(() => {
      clearInterval(this.messageIntervalId);
      this.stopMesh();
    });
  }

  private stopMesh(): void {
    cancelAnimationFrame(this.meshRafId);
    this.meshResizeObserver?.disconnect();
    this.meshResizeObserver = undefined;
  }

  private startMesh(theme: Theme): void {
    const canvas = this.canvasRef().nativeElement;
    const container = this.containerRef().nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const palette = PALETTES[theme];

    let width = 0;
    let height = 0;
    let prevCx = 0;
    let prevCy = 0;
    let pendingWidth = container.clientWidth;
    let pendingHeight = container.clientHeight;
    let needsResize = pendingWidth > 0 && pendingHeight > 0;
    const particles: Particle[] = [];

    const spawnParticles = (): void => {
      particles.length = 0;
      const cx = width / 2;
      const cy = height / 2;
      const clusterRadius = clusterRadiusFor(width, height);
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        // Bias the radius toward the center so the cloud is denser around the logo.
        const angle = Math.random() * Math.PI * 2;
        const radius = CENTER_GUARD + Math.pow(Math.random(), 1.5) * (clusterRadius - CENTER_GUARD);
        particles.push({
          x: cx + Math.cos(angle) * radius,
          y: cy + Math.sin(angle) * radius,
          vx: (Math.random() - 0.5) * 0.22,
          vy: (Math.random() - 0.5) * 0.22,
          r: 0.9 + Math.random() * 1.5,
          energy: 0,
        });
      }
    };

    // Reallocating the canvas bitmap clears it. Doing that in the ResizeObserver callback
    // (which fires before paint) would show an empty canvas for a frame -> flicker. So we
    // only flag the new size here and apply it inside the RAF tick, right before drawing.
    const applyResize = (): void => {
      width = pendingWidth;
      height = pendingHeight;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cx = width / 2;
      const cy = height / 2;

      if (!particles.length) {
        // First valid layout: create the cloud once.
        spawnParticles();
      } else {
        // Keep the existing particles and shift them by how far the center moved, so the
        // cloud stays put relative to the logo and the per-frame boundary forces can
        // gently settle them into the new bounds (no respawn).
        const shiftX = cx - prevCx;
        const shiftY = cy - prevCy;
        for (const p of particles) {
          p.x += shiftX;
          p.y += shiftY;
        }
      }

      prevCx = cx;
      prevCy = cy;
    };

    this.meshResizeObserver = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      pendingWidth = Math.round(rect.width);
      pendingHeight = Math.round(rect.height);
      needsResize = pendingWidth > 0 && pendingHeight > 0;
    });
    this.meshResizeObserver.observe(container);

    const t0 = performance.now();

    const frame = (now: number): void => {
      if (needsResize) {
        applyResize();
        needsResize = false;
      }

      if (!width || !height) {
        this.meshRafId = requestAnimationFrame(frame);

        return;
      }

      const t = now - t0;
      const cx = width / 2;
      const cy = height / 2;
      const maxR = Math.hypot(width, height) * 0.55;
      const pulsePhase = (t % PULSE_PERIOD) / PULSE_PERIOD;
      const pulseR = pulsePhase * maxR;
      const pulseFade = Math.pow(1 - pulsePhase, 1.4);

      ctx.clearRect(0, 0, width, height);

      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.min(width, height) * 0.5);
      grd.addColorStop(
        0,
        `rgba(${palette.ring[0]},${palette.ring[1]},${palette.ring[2]},${palette.coreA})`,
      );
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, width, height);

      if (pulseFade > 0.04 && pulseR > 30) {
        const ringA = pulseFade * 0.35;
        ctx.beginPath();
        ctx.arc(cx, cy, pulseR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${palette.ring[0]},${palette.ring[1]},${palette.ring[2]},${ringA.toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      const clusterRadius = clusterRadiusFor(width, height);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;

        const ox = p.x - cx;
        const oy = p.y - cy;
        const d = Math.hypot(ox, oy) || 1;

        // Soft outer boundary: gently steer particles back toward the cluster so they
        // gravitate around the center instead of drifting off to the screen edges.
        if (d > clusterRadius) {
          const overshoot = (d - clusterRadius) / clusterRadius;
          const pull = 0.01 * (1 + overshoot);
          p.vx -= (ox / d) * pull;
          p.vy -= (oy / d) * pull;
        } else if (d < CENTER_GUARD) {
          // Soft inner guard: keep dots from crawling over the logo.
          const push = 0.02;
          p.vx += (ox / d) * push;
          p.vy += (oy / d) * push;
        }

        // Clamp speed so the cloud keeps a calm, perpetual drift.
        const speed = Math.hypot(p.vx, p.vy);
        if (speed > MAX_PARTICLE_SPEED) {
          p.vx = (p.vx / speed) * MAX_PARTICLE_SPEED;
          p.vy = (p.vy / speed) * MAX_PARTICLE_SPEED;
        }

        const delta = Math.abs(d - pulseR);
        p.energy = Math.max(0, 1 - delta / PULSE_BAND) * pulseFade;
      }

      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dsq = dx * dx + dy * dy;
          if (dsq < LINK_DIST * LINK_DIST) {
            const d = Math.sqrt(dsq);
            const proximity = 1 - d / LINK_DIST;
            const energy = Math.max(a.energy, b.energy);
            const alpha = proximity * (palette.lineBaseA + energy * 0.7);
            ctx.strokeStyle = `rgba(${palette.line[0]},${palette.line[1]},${palette.line[2]},${alpha.toFixed(3)})`;
            ctx.lineWidth = 0.6 + energy * 0.7;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const p of particles) {
        const hot = p.energy;
        const r = p.r + hot * 1.6;
        const c = hot > 0.15 ? palette.dotHot : palette.dotBase;
        ctx.fillStyle = `rgba(${c[0]},${c[1]},${c[2]},${c[3]})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      this.meshRafId = requestAnimationFrame(frame);
    };

    this.meshRafId = requestAnimationFrame(frame);
  }
}
